import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { isGalitoPartnerLink } from '@shared/extensions/extensions';
import { CustomerService } from '@shared/webapi/services/customer.service';
import { Observable } from 'rxjs';
import { ClientFacade } from '../../../../store/client-store/client.facade';
import { ClientType } from '../../../../store/client-store/client.models';

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'app-client-prospect-switcher',
  templateUrl: './client-prospect-switcher.component.html',
  styleUrls: ['./client-prospect-switcher.component.scss']
})
export class ClientProspectSwitcherComponent {
  readonly clientType = ClientType;
  readonly clientType$: Observable<ClientType | null>;
  private selectedClientType: ClientType | null = null;

  constructor(
    private clientFacade: ClientFacade,
    private customerService: CustomerService,
    private route: ActivatedRoute,
    private router: Router
  ) {
    this.clientType$ = this.clientFacade.clientType$;

    this.clientFacade.clientType$.pipe(untilDestroyed(this)).subscribe(clientType => {
      this.selectedClientType = clientType;
    });
  }

  ngOnInit(): void {
    if (isGalitoPartnerLink(this.route.snapshot.queryParamMap)) {
      this.setClientType(ClientType.Prospect);
      return;
    }

    this.restoreClientTypeFromProfile();
  }

  setClientType(clientType: ClientType): void {
    if (this.selectedClientType === clientType) {
      return;
    }

    void this.resetMemberStateOnProfileTypeChange(clientType).then(() => {
      this.clientFacade.setClientType(clientType);
    });
  }

  private async resetMemberStateOnProfileTypeChange(clientType: ClientType): Promise<void> {
    const isSwitchingFromContractToProspect =
      this.selectedClientType === ClientType.Contract && clientType === ClientType.Prospect;
    const hasExistingMemberData = !!this.customerService.userData?.socialNumberA;

    if (!isSwitchingFromContractToProspect || !hasExistingMemberData) {
      return;
    }

    await this.customerService.clearAllData();
    await this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { userId: null },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  private restoreClientTypeFromProfile(): void {
    if (this.selectedClientType !== null) {
      return;
    }

    const userId = this.route.snapshot.queryParamMap.get('userId')?.trim();
    const profile = this.customerService.userData;

    if (!userId || !profile?.uniqueId || profile.uniqueId !== userId) {
      return;
    }

    const restoredClientType =
      profile.isMember === true || !!profile.socialNumberA ? ClientType.Contract : ClientType.Prospect;

    this.clientFacade.restoreClientType(restoredClientType);
  }
}
